import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { ExpenseStatus } from '@/lib/types';
import { useExpense } from '@/hooks/useExpense';
import { useRepositories } from '@/context/RepositoryContext';
import { Modal, StatusBadge, StatusSwitcher, CategoryChip } from '@/components/hearthline/primitives';
import { Icons } from '@/components/icons';
import { fmtUSD, fmtDate, fmtTime } from '@/lib/format';

export function ExpenseDetailRoute() {
  const { expenseId } = useParams();
  const navigate = useNavigate();
  const { expenseRepository } = useRepositories();
  const { expense, loading, reload } = useExpense(expenseId);
  const [busy, setBusy] = useState(false);

  const close = () => navigate('/expenses');

  if (loading) {
    return (
      <Modal open title="Receipt" onClose={close}>
        <div className="empty"><div className="ttl">Loading…</div></div>
      </Modal>
    );
  }

  if (!expense) {
    return (
      <Modal open title="Receipt" onClose={close}>
        <div className="empty">
          <Icons.Receipt size={26} stroke="var(--faint)" />
          <div className="ttl">Receipt not found</div>
        </div>
      </Modal>
    );
  }

  const r = expense;

  const changeStatus = async (status: ExpenseStatus) => {
    if (status === r.status) return;
    setBusy(true);
    await expenseRepository.updateExpenseStatus(r.id, status);
    reload();
    setBusy(false);
  };

  const remove = async () => {
    if (!window.confirm('Delete this receipt? This cannot be undone.')) return;
    setBusy(true);
    await expenseRepository.deleteExpense(r.id);
    reload();
    navigate('/expenses');
  };

  return (
    <Modal
      open
      title={r.store_name || 'Receipt'}
      onClose={close}
      headerExtras={
        <>
          <StatusBadge status={r.status} />
          <button className="btn sm ghost" title="Edit" onClick={() => navigate(`/expenses/${r.id}/edit`)} disabled={busy}>
            <Icons.Pencil size={14} />
          </button>
          <button className="btn sm ghost" title="Delete" onClick={remove} disabled={busy}>
            <Icons.Trash size={14} />
          </button>
        </>
      }
    >
      <div className="rdetail">
        <div>
          <div className="rphoto">
            {r.receipt_url ? (
              <img src={r.receipt_url} alt="Receipt" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            ) : (
              <div className="receipt">
                <div className="ctr" style={{ fontWeight: 600, fontSize: 11.5, letterSpacing: '0.04em' }}>{r.store_name || 'RECEIPT'}</div>
                <hr />
                <div style={{ fontSize: 9.5, color: 'var(--muted)', marginBottom: 4 }}>{fmtDate(r.transaction_date)} · {fmtTime(r.transaction_date)}</div>
                {r.items.slice(0, 6).map((it, i) => (
                  <div key={i} className="line">
                    <span className="nm">{it.name.slice(0, 20)}{it.name.length > 20 ? '…' : ''}</span>
                    <span>{it.total.toFixed(2)}</span>
                  </div>
                ))}
                {r.items.length > 6 && <div style={{ color: 'var(--muted)', fontSize: 9.5 }}>… +{r.items.length - 6} more</div>}
                <hr />
                <div className="line"><span>SUBTOTAL</span><span>{r.subtotal.toFixed(2)}</span></div>
                <div className="line"><span>TAX</span><span>{r.tax.toFixed(2)}</span></div>
                <div className="line" style={{ fontWeight: 600, marginTop: 3 }}><span>TOTAL</span><span>${r.total.toFixed(2)}</span></div>
                <hr />
                <div className="ctr" style={{ fontSize: 9, color: 'var(--muted)' }}>THANK YOU FOR YOUR PURCHASE</div>
              </div>
            )}
          </div>
        </div>

        <div>
          <div className="meta-grid">
            <div><div className="k">Transaction</div><div className="v">{fmtDate(r.transaction_date)}</div></div>
            <div><div className="k">Time</div><div className="v">{fmtTime(r.transaction_date)}</div></div>
            <div><div className="k">Store</div><div className="v">{r.store_name || '—'}</div></div>
            <div><div className="k">Items</div><div className="v">{r.items.length}</div></div>
            <div style={{ gridColumn: '1 / -1' }}>
              <div className="k" style={{ marginBottom: 6 }}>Status</div>
              <StatusSwitcher value={r.status} onChange={changeStatus} />
            </div>
          </div>

          <table className="items-tbl">
            <thead>
              <tr>
                <th style={{ width: '44%' }}>Item</th>
                <th style={{ width: '26%' }}>Category</th>
                <th style={{ width: '8%', textAlign: 'right' }}>Qty</th>
                <th style={{ width: '10%', textAlign: 'right' }}>Unit</th>
                <th style={{ width: '12%', textAlign: 'right' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {r.items.map((it, i) => (
                <tr key={i}>
                  <td>{it.name}</td>
                  <td><CategoryChip category={it.category} /></td>
                  <td className="num">{it.quantity}</td>
                  <td className="num">{fmtUSD(it.unit_price)}</td>
                  <td className="num" style={{ fontWeight: 500 }}>{fmtUSD(it.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="totals">
            <div className="row"><span>Subtotal</span><span className="num">{fmtUSD(r.subtotal)}</span></div>
            {r.discount ? <div className="row"><span>Discount</span><span className="num">−{fmtUSD(r.discount)}</span></div> : null}
            <div className="row"><span>Tax</span><span className="num">{fmtUSD(r.tax)}</span></div>
            <div className="row grand"><span>Total</span><span className="num">{fmtUSD(r.total)}</span></div>
          </div>

          {r.notes && (
            <div style={{ marginTop: 14, padding: 12, background: 'var(--bg-2)', borderRadius: 8, fontSize: 13, color: 'var(--ink-2)' }}>
              <div style={{ fontSize: 10.5, color: 'var(--muted)', letterSpacing: '0.05em', textTransform: 'uppercase', fontWeight: 600, marginBottom: 4 }}>Notes</div>
              {r.notes}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
